import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './SignupSubscribePage.css';
import './LoginPage.css'; // Reusing some base inputs

const SignupSubscribePage = () => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState('free');
    
    // Register Form State
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        usage_type: 'Architecte'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const USAGE_OPTIONS = [
        "Architecte",
        "Etudiant",
        "Designer",
        "Architecture d'interieur",
        "Paysagiste",
        "Urbaniste",
        "Autre"
    ];

    const plans = [
        { id: 'free', name: 'Essai Gratuit', price: '0', features: ['5 Images', 'Résolution Standard', 'Usage Personnel'] },
        { id: 'standard', name: 'Standard', price: '9.90', features: ['50 Renders', 'Standard Res', 'Usage Commercial'] },
        { id: 'pro', name: 'Pro', price: '19.90', features: ['200 Renders', '4K Res', 'Capture 3D', 'Support Prio'] },
    ];

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Form Validation
        if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
            setError("Veuillez remplir tous les champs.");
            return;
        }
        if (formData.password.length < 8) {
            setError("Le mot de passe doit contenir au moins 8 caractères.");
            return;
        }
        const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
        if (!specialCharRegex.test(formData.password)) {
            setError("Le mot de passe doit contenir au moins un caractère spécial.");
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        // Only allowing free plan via this form for now (as standard/pro are locked simulated)
        if (selectedPlan !== 'free') {
             setError("Seul l'essai gratuit est disponible pour l'instant.");
             return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                username: formData.username,
                email: formData.email,
                password: formData.password,
                usage_type: formData.usage_type
            };

            await axios.post(`${API_URL}/api/register/`, payload);
            setSuccess(true);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.data) {
                const errorEntries = Object.entries(err.response.data);
                if (errorEntries.length > 0) {
                    const [field, messages] = errorEntries[0];
                    const msg = Array.isArray(messages) ? messages[0] : messages;

                    const fieldNames = {
                        username: "Nom d'utilisateur",
                        email: "Email",
                        password: "Mot de passe",
                        confirmPassword: "Confirmation du mot de passe",
                        usage_type: "Type d'usage",
                        non_field_errors: "Erreur"
                    };
                    const fieldName = fieldNames[field] || field;

                    let displayMsg = msg;
                    if (msg === "This field is required.") displayMsg = "Ce champ est requis.";
                    else if (msg.includes("A user with that username already exists")) displayMsg = "Ce nom d'utilisateur est déjà pris.";
                    else if (msg.includes("Enter a valid email address")) displayMsg = "Adresse email invalide.";
                    else if (msg.includes("user with this email address already exists")) displayMsg = "Un compte existe déjà avec cet email.";

                    setError(`${fieldName} : ${displayMsg}`);
                } else {
                    setError("Erreur inconnue lors de l'inscription.");
                }
            } else {
                setError("Erreur serveur. Veuillez réessayer.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="signup-subscribe-container">
                <div className="signup-subscribe-card" style={{ maxWidth: '400px' }}>
                    <h1 className="login-title">Vérification Requise</h1>
                    <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                        Votre compte a été créé.<br />
                        Un code de vérification a été envoyé à <strong>{formData.email}</strong>.
                    </p>
                    <button onClick={() => navigate('/verify-email')} className="login-btn">
                        Saisir le code
                    </button>
                    <p style={{ fontSize: '0.9rem', marginTop: '10px', color: '#666', textAlign: 'center' }}>
                        Le code est valide pendant 24h.
                    </p>
                </div>
            </div>
        );
    }

    // SVG Icons
    const EyeOpen = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
        </svg>
    );

    const EyeClosed = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
            <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
    );

    return (
        <div className="signup-subscribe-container">
            <div className="simple-nav">
                <button className="brand-home-link" onClick={() => navigate('/')}>ARCHIVIZAI</button>
            </div>
            
            <div className="signup-subscribe-card">
                <div className="signup-subscribe-header">
                    <h1 className="signup-subscribe-title">Commencer Maintenant</h1>
                </div>

                <div className="split-content">
                    {/* LEFT PANEL: Plan Selection */}
                    <div className="left-panel">
                        <h2 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', marginTop: 0 }}>1. Choisissez votre abonnement</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {plans.map(plan => (
                                <div
                                    key={plan.id}
                                    className={`mini-plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${plan.id !== 'free' ? 'locked' : ''}`}
                                    onClick={() => {
                                        if (plan.id === 'free') setSelectedPlan(plan.id);
                                    }}
                                >
                                    <div className="mini-plan-name">{plan.name}</div>
                                    <div className="mini-plan-price">{plan.price}€<span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>{plan.id !== 'free' ? '/mois' : ''}</span></div>
                                    <ul className="mini-plan-features">
                                        {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <div className="mini-radio"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT PANEL: Registration Form */}
                    <div className="right-panel">
                        <h2 style={{ fontSize: '1.2rem', marginBottom: '1.5rem', marginTop: 0 }}>2. Créez votre compte</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label htmlFor="usage_type">Vous êtes ?</label>
                                <select
                                    id="usage_type"
                                    className="input-field"
                                    value={formData.usage_type}
                                    onChange={handleChange}
                                    style={{ backgroundColor: 'white' }}
                                >
                                    {USAGE_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-group">
                                <label htmlFor="username">Nom d'utilisateur</label>
                                <input
                                    type="text"
                                    id="username"
                                    className="input-field"
                                    placeholder="Pseudo"
                                    value={formData.username}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="input-field"
                                    placeholder="votre@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="input-group" style={{ display: 'flex', gap: '1rem', marginBottom: '0' }}>
                                <div style={{ flex: 1 }}>
                                    <label htmlFor="password">Mot de passe</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            className="input-field"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            minLength={8}
                                            style={{ width: '100%', paddingRight: '40px', boxSizing: 'border-box' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={togglePasswordVisibility}
                                            style={{
                                                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer', color: '#000', padding: 0, display: 'flex', alignItems: 'center'
                                            }}
                                        >
                                            {showPassword ? <EyeOpen /> : <EyeClosed />}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ flex: 1 }}>
                                    <label htmlFor="confirmPassword">Confirmer</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            id="confirmPassword"
                                            className="input-field"
                                            placeholder="••••••••"
                                            value={formData.confirmPassword || ''}
                                            onChange={handleChange}
                                            required
                                            style={{ width: '100%', paddingRight: '40px', boxSizing: 'border-box' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={toggleConfirmPasswordVisibility}
                                            style={{
                                                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer', color: '#000', padding: 0, display: 'flex', alignItems: 'center'
                                            }}
                                        >
                                            {showConfirmPassword ? <EyeOpen /> : <EyeClosed />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: '1.5rem' }}>
                                {error && <div className="error-message" style={{ color: 'red', marginBottom: '15px', fontSize: '0.9rem' }}>{error}</div>}

                                <button type="submit" className="login-btn" disabled={loading} style={{ marginTop: '0.5rem', fontSize: '1rem', padding: '1rem' }}>
                                    {loading ? 'Création...' : (selectedPlan === 'free' ? 'Commencer l\'Essai Gratuit' : 'Passer au paiement')}
                                </button>
                            </div>
                            
                            <div className="login-footer" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                                <p>Déjà un compte ? <button type="button" onClick={() => navigate('/connexion')} className="link-btn">Se connecter</button></p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupSubscribePage;
